import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { paginateQuery } from '@/lib/paginateSupabase';

// Alcance del auditor logueado: cohortes asignadas y sus participantes.
//
// La RLS NO basta para aislar la vista de auditor: las policies se suman, así
// que además de las cohortes asignadas el auditor ve las cohortes PÚBLICAS
// ("Auth read public cohorts") y, si también tiene otro rol (ej. admin de su
// empresa), todo lo que ese rol le da. La vista de auditor debe mostrar SOLO lo
// asignado, así que las listas del frontend se filtran con estos ids.
export function useAuditorCohortIds() {
  const { currentRole, user } = useAuth();
  const isAuditor = currentRole === 'auditor';

  const query = useQuery({
    queryKey: ['auditor-scope', user?.id],
    queryFn: async (): Promise<{ cohortIds: string[]; participantIds: string[] }> => {
      const { data, error } = await (supabase as any)
        .from('cohort_auditors')
        .select('cohort_id')
        .eq('user_id', user!.id);
      if (error) throw error;
      const cohortIds = (data || []).map((r: any) => r.cohort_id as string);
      if (cohortIds.length === 0) return { cohortIds, participantIds: [] };

      const parts = await paginateQuery<{ user_id: string }>((from, to) =>
        supabase
          .from('cohort_participants')
          .select('user_id')
          .in('cohort_id', cohortIds)
          .order('user_id')
          .range(from, to)
      );
      const participantIds = [...new Set(parts.map((p) => p.user_id))];
      return { cohortIds, participantIds };
    },
    enabled: isAuditor && !!user?.id,
    staleTime: 60_000,
  });

  return {
    isAuditor,
    cohortIds: query.data?.cohortIds ?? [],
    participantIds: query.data?.participantIds ?? [],
    isLoading: query.isLoading,
    isReady: !isAuditor || query.isSuccess,
  };
}
